import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext, API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Camera, Eye, BookOpen, Trash2, Send, Link2, XCircle, Copy, MessageCircle,
  Mail, CheckCircle2, Loader2, ImageOff,
} from "lucide-react";
import { toast } from "sonner";
import { PhotoCaptureDialog } from "./photos/PhotoCaptureDialog";

const token = () => localStorage.getItem("token");
const authImg = (id, thumb = true) =>
  `${API}/photos/${id}/file?${thumb ? "thumb=true&" : ""}auth=${token()}`;

const STATUS_BADGE = {
  not_sent: { label: "Not sent", cls: "bg-slate-100 text-slate-600" },
  sent: { label: "Sent", cls: "bg-blue-100 text-blue-700" },
  viewed: { label: "Viewed", cls: "bg-green-100 text-green-700" },
};

export default function SitePhotos() {
  const { user } = useContext(AuthContext);
  const canAccess = user?.role === "admin" || user?.role === "company_owner" || user?.role === "site_manager";

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [photos, setPhotos] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("gallery");

  const [area, setArea] = useState("all");
  const [range, setRange] = useState("all");
  const [clientOnly, setClientOnly] = useState(false);

  const [captureOpen, setCaptureOpen] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sendDialog, setSendDialog] = useState({ open: false, url: "", update: null });

  const currentSite = sites.find((s) => s.id === selectedSite) || null;

  useEffect(() => {
    if (canAccess) loadSites();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedSite) {
      loadPhotos();
      loadUpdates();
    }
  }, [selectedSite, area, range, clientOnly]); // eslint-disable-line

  const loadSites = async () => {
    try {
      const res = await fetch(`${API}/sites`, { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      setSites(data);
      if (data.length) setSelectedSite(data[0].id);
    } catch {
      toast.error("Failed to load sites");
    } finally {
      setLoading(false);
    }
  };

  const loadPhotos = async () => {
    const params = new URLSearchParams();
    if (area !== "all") params.append("area", area);
    if (range !== "all") params.append("range", range);
    if (clientOnly) params.append("client_visible_only", "true");
    const res = await fetch(`${API}/sites/${selectedSite}/photos?${params}`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.ok) setPhotos(await res.json());
  };

  const loadUpdates = async () => {
    const res = await fetch(`${API}/sites/${selectedSite}/photo-updates`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.ok) setUpdates(await res.json());
  };

  const allAreas = useMemo(
    () => [...new Set(photos.map((p) => p.area_tag).filter(Boolean))],
    [photos]
  );

  const grouped = useMemo(() => {
    const m = {};
    photos.forEach((p) => {
      (m[p.date] = m[p.date] || []).push(p);
    });
    return Object.entries(m).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [photos]);

  const togglePhotoFlag = async (photo, field) => {
    const body =
      field === "diary"
        ? { attach_to_diary: !photo.diary_linked }
        : { client_visible: !photo.client_visible };
    const res = await fetch(`${API}/photos/${photo.id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token()}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const updated = await res.json();
      setViewPhoto(updated);
      loadPhotos();
      loadUpdates();
    } else {
      toast.error("Could not update photo");
    }
  };

  const doDelete = async () => {
    const res = await fetch(`${API}/photos/${confirmDelete.id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.ok) {
      toast.success("Photo deleted");
      setViewPhoto(null);
      loadPhotos();
      loadUpdates();
    } else {
      toast.error("Could not delete");
    }
    setConfirmDelete(null);
  };

  const sendUpdate = async (upd) => {
    const res = await fetch(`${API}/photo-updates/${upd.id}/send`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Could not generate link");
      return;
    }
    const data = await res.json();
    await loadUpdates();
    setSendDialog({ open: true, url: `${window.location.origin}${data.path}`, update: upd });
  };

  const revokeUpdate = async (upd) => {
    const res = await fetch(`${API}/photo-updates/${upd.id}/revoke`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.ok) {
      toast.success("Link revoked");
      loadUpdates();
    }
  };

  if (!canAccess) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="site-photos-denied">
        <h2 className="text-2xl font-bold text-slate-800">Site Photos</h2>
        <p className="text-slate-500 mt-2">Available to Admins and Site Managers only.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="site-photos-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-amber-600 flex items-center justify-center">
          <Camera className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Site Photos</h1>
          <p className="text-sm text-slate-500">
            General site photo record — progress shots & visual proof of works
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="w-full sm:max-w-xs">
          <Label className="text-xs text-slate-500">Site</Label>
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger data-testid="site-photos-site-select">
              <SelectValue placeholder="Select a site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          className="bg-amber-500 hover:bg-amber-600 ml-auto"
          disabled={!selectedSite}
          onClick={() => setCaptureOpen(true)}
          data-testid="add-photos-btn"
        >
          <Camera className="w-4 h-4 mr-2" /> Add photos
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
          <TabsTrigger value="updates" data-testid="tab-client-updates">Client Updates</TabsTrigger>
        </TabsList>

        {/* GALLERY */}
        <TabsContent value="gallery" className="mt-4">
          <div className="flex flex-wrap gap-3 mb-4">
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger className="w-40" data-testid="filter-area"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All areas</SelectItem>
                {allAreas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-40" data-testid="filter-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm cursor-pointer px-2">
              <Checkbox checked={clientOnly} onCheckedChange={(v) => setClientOnly(!!v)} data-testid="filter-client-visible" />
              Client-visible only
            </label>
          </div>

          {grouped.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-slate-400">
              <ImageOff className="w-10 h-10 mx-auto mb-3" />
              No photos yet. Tap "Add photos" to capture site progress.
            </CardContent></Card>
          ) : (
            grouped.map(([date, list]) => (
              <div key={date} className="mb-6" data-testid={`photo-group-${date}`}>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">
                  {new Date(date).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {list.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setViewPhoto(p)}
                      className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group"
                      data-testid={`photo-thumb-${p.id}`}
                    >
                      <img src={authImg(p.id)} alt={p.area_tag || ""} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                      {p.client_visible && (
                        <span className="absolute top-1.5 right-1.5 bg-blue-600 rounded-full p-1" title="Visible to client">
                          <Eye className="w-3 h-3 text-white" />
                        </span>
                      )}
                      {p.diary_linked && (
                        <span className="absolute bottom-1.5 left-1.5 bg-green-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded flex items-center gap-0.5" title="In diary">
                          <BookOpen className="w-2.5 h-2.5" /> Diary
                        </span>
                      )}
                      {p.area_tag && (
                        <span className="absolute bottom-1.5 right-1.5 bg-black/55 text-white text-[10px] px-1.5 py-0.5 rounded">
                          {p.area_tag}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* CLIENT UPDATES */}
        <TabsContent value="updates" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {updates.length === 0 ? (
                <div className="py-16 text-center text-slate-400">
                  No client updates yet. Mark photos "Visible to client" to build one.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="photo-updates-table">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="p-3">Date</th>
                      <th className="p-3">Photos</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updates.map((u) => {
                      const sb = STATUS_BADGE[u.status] || STATUS_BADGE.not_sent;
                      return (
                        <tr key={u.id} className="border-b last:border-0" data-testid={`update-row-${u.id}`}>
                          <td className="p-3 font-medium text-slate-800">
                            {new Date(u.date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
                          </td>
                          <td className="p-3">{u.photo_count}</td>
                          <td className="p-3">
                            <Badge className={sb.cls}>{sb.label}</Badge>
                            {u.acknowledged && <Badge className="ml-1 bg-emerald-100 text-emerald-700">Acknowledged</Badge>}
                          </td>
                          <td className="p-3 text-right space-x-1">
                            <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => sendUpdate(u)} data-testid={`send-update-${u.id}`}>
                              <Send className="w-3.5 h-3.5 mr-1" /> {u.token ? "Resend" : "Send"}
                            </Button>
                            {u.token_active && (
                              <Button size="sm" variant="outline" onClick={() => revokeUpdate(u)} data-testid={`revoke-update-${u.id}`}>
                                <XCircle className="w-3.5 h-3.5 mr-1" /> Revoke
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Capture dialog */}
      <PhotoCaptureDialog
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        siteId={selectedSite}
        areaSuggestions={allAreas}
        onUploaded={() => { loadPhotos(); loadUpdates(); }}
      />

      {/* Full photo view */}
      <Dialog open={!!viewPhoto} onOpenChange={() => setViewPhoto(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="photo-view-dialog">
          {viewPhoto && (
            <>
              <DialogHeader>
                <DialogTitle>{viewPhoto.area_tag || "Photo"}</DialogTitle>
                <DialogDescription>{new Date(viewPhoto.date).toLocaleDateString("en-AU")}</DialogDescription>
              </DialogHeader>
              <img src={authImg(viewPhoto.id, false)} alt="" className="w-full rounded-xl bg-slate-100" />
              <div className="space-y-3 mt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={viewPhoto.diary_linked} onCheckedChange={() => togglePhotoFlag(viewPhoto, "diary")} data-testid="view-toggle-diary" />
                  Attach to diary entry
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={viewPhoto.client_visible} onCheckedChange={() => togglePhotoFlag(viewPhoto, "client")} data-testid="view-toggle-client" />
                  Visible to client
                </label>
                <Button variant="outline" className="text-rose-600 border-rose-200" onClick={() => setConfirmDelete(viewPhoto)} data-testid="delete-photo-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete photo
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send link dialog */}
      <PhotoSendDialog
        state={sendDialog}
        site={currentSite}
        onClose={() => setSendDialog({ open: false, url: "", update: null })}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-photo-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PhotoSendDialog({ state, site, onClose }) {
  const { open, url, update } = state;
  if (!update) return null;
  const clientName = site?.client_name || "there";
  const dateLabel = update ? new Date(update.date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "";
  const message =
    `Hi ${clientName},\n` +
    `Here are the latest site photos from ${site?.name || "your project"} (${dateLabel}).\n` +
    `View them here: ${url}\n` +
    `No login required — just open the link.\n` +
    `Thanks,\nSiteProApp`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  };
  const openWhatsApp = () => {
    const phone = (site?.client_phone || "").replace(/[^0-9]/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
  };
  const openEmail = () => {
    window.location.href = `mailto:${site?.client_email || ""}?subject=${encodeURIComponent(`Site photos — ${dateLabel}`)}&body=${encodeURIComponent(message)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="photo-send-dialog">
        <DialogHeader>
          <DialogTitle>Send photos to {site?.client_name || "client"}</DialogTitle>
          <DialogDescription>
            {site?.client_email}{site?.client_phone ? ` · ${site.client_phone}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Link preview</Label>
            <div className="flex gap-2 mt-1">
              <Input readOnly value={url} className="text-xs" data-testid="photo-send-url" />
              <Button variant="outline" size="icon" onClick={copyLink} data-testid="photo-send-copy"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={openWhatsApp} className="bg-green-600 hover:bg-green-700" data-testid="photo-send-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
            <Button variant="outline" onClick={openEmail} data-testid="photo-send-email">
              <Mail className="w-4 h-4 mr-2" /> Email
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            SiteProApp doesn't send messages directly — copy the link or open WhatsApp/email with a pre-filled message.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
